import React from 'react'

import { useCallbackRef } from '../../utils/useCallbackRef'
import { useEventListener } from '../../utils/useEventListener'
import { setValue } from '../../utils/input'
import { format, TextFormatter } from './formatter'

export interface TextProps {
  value?: string
  formatters: Array<TextFormatter>
}

export interface TextGetProps {
  id?: string
  type?: HTMLInputElement['type']
  inputRef: React.RefCallback<HTMLInputElement>
  name: string
}

export interface TextReturnValues {
  ref: React.RefCallback<HTMLInputElement>
  value: string
  getProps: () => TextGetProps
}

const useText = ({ value = '', formatters = [] }: TextProps) => {
  const [formattedValue, setFormattedValue] = React.useState(() => value)
  const { callbackRef, elementRef } = useCallbackRef<HTMLInputElement>(
    (element) => {
      const formatted = format(value, formatters)

      setValue(element, formatted)
      setFormattedValue(formatted)
    },
  )
  const inputRef = elementRef.current

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!inputRef) {
        return
      }

      const formatted = format(e.target.value, formatters)

      setValue(inputRef, formatted)
      setFormattedValue(formatted)
    },
    [formatters, inputRef],
  )

  useEventListener('input', handleChange, inputRef)

  React.useEffect(() => {
    if (!inputRef) {
      return
    }

    const formatted = format(value, formatters)

    setValue(inputRef, formatted)
    setFormattedValue(formatted)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const getProps = React.useCallback(
    (props = {}): TextGetProps => {
      const { name, id = name } = props
      return {
        id,
        name,
        type: 'text',
        inputRef: callbackRef,
      }
    },
    [callbackRef],
  )

  return {
    ref: callbackRef,
    value: formattedValue,
    getProps,
  }
}

export { useText }
